// App.js
import React from "react";
import { Box } from "@mui/material";
import Dashboard from "./components/Dashboard";
import {
  NotificationProvider,
  PermissionCenterProvider,
  AuthLayout,
  DarkModeProvider,
} from "@1823-partners/core";
import api from "./api";

/*
 * MUI Chip bug workaround, applied at the theme so it also covers chips this app does not render.
 * Chip's root slot wires up
 *   onClick: event => { handlers.onClick?.(event); onClick(event); }
 * with NO guard on the second call, so clicking a Chip that was given no handler throws
 * "onClick is not a function" and takes the tab down with the dev overlay.
 *
 * The chips that hit this are the Granted/Missing labels inside core's permissions drawer, which
 * we cannot patch from here -- the drawer ships compiled inside @1823-partners/core. MUI applies
 * defaultProps to every instance of a component, including ones rendered from another bundle, so
 * supplying the no-op here reaches them. See READ_ONLY_CHIP in components/remittance/
 * RemittanceDashboard.jsx, which does the same thing for this app's own chips.
 *
 * clickable:false is the other half and is not redundant: MUI computes
 *   clickable = clickableProp !== false && onClick ? true : clickableProp
 * so the no-op alone would promote every chip in the app to a ButtonBase with a ripple and a
 * pointer cursor. Recon has no genuinely clickable chip today; one added later must pass
 * clickable explicitly.
 *
 * Module scope, not inline: DarkModeProvider memoises the theme on this object's identity, so a
 * fresh literal per render would rebuild the theme on every render.
 */
const THEME_OPTIONS = {
  components: {
    MuiChip: { defaultProps: { clickable: false, onClick: () => {} } },
  },
};

export default function App() {
  return (
    <DarkModeProvider appName="recon" themeOptions={THEME_OPTIONS}>
      <NotificationProvider>
        {/* Mounts the permissions drawer (the toolbar's shield button) and,
            because it wraps PermissionRequestProvider itself, keeps turning the
            "Request access" button on permission-denied toasts into an in-app
            request. Do not also mount PermissionRequestProvider -- that nests a
            second copy. Inside NotificationProvider because the request path
            reports its outcome as a toast. */}
        <PermissionCenterProvider api={api} appName="recon">
          <AuthLayout
            title="Recon"
            appBarSx={{ bgcolor: "background.paper" }}
          >
            {({ userInfo }) => (
              <Box sx={{ p: 3 }}>
                <Dashboard userId={userInfo?.userDetails} />
              </Box>
            )}
          </AuthLayout>
        </PermissionCenterProvider>
      </NotificationProvider>
    </DarkModeProvider>
  );
}
